import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getDriverSafetyAssessment, editDriverSafetyAssessment } from '@/service/driverArchive';
import { message } from 'antd';
import SafetyAssessmentTable from './SafetyAssessmentTable';

/**
 * 安全评估数据项
 */
export interface AssessmentDataItem {
  key: string;
  item: string;
  details: string | number;
  score: number;
  proportionScore: number;
}

/**
 * 初始空数据
 */
const INITIAL_INDIVIDUAL_INFO: AssessmentDataItem[] = [
  { key: 'age', item: '年龄分布', details: 0, score: 0, proportionScore: 0 },
  { key: 'drivingExperience', item: '驾龄分布', details: 0, score: 0, proportionScore: 0 },
  { key: 'safetyAwareness', item: '安全意识', details: 0, score: 0, proportionScore: 0 },
  { key: 'personalityTrait', item: '性格', details: 0, score: 0, proportionScore: 0 },
  { key: 'communicationSkill', item: '沟通能力', details: 0, score: 0, proportionScore: 0 },
];

const INITIAL_DRIVING_VIOLATIONS: AssessmentDataItem[] = [
  { key: 'speedingCountPer100km', item: '超速', details: 0, score: 0, proportionScore: 0 },
  { key: 'phoneUseCountPer100km', item: '接打电话', details: 0, score: 0, proportionScore: 0 },
];

const INITIAL_ACCIDENT_HISTORY: AssessmentDataItem[] = [
  { key: 'claimCount', item: '档案登记的出险次数', details: 0, score: 0, proportionScore: 0 },
  { key: 'afterServiceScore', item: '售后服务中的分值', details: 0, score: 0, proportionScore: 0 },
];

const INITIAL_DRIVING_BEHAVIOR: AssessmentDataItem[] = [
  { key: 'frequentLaneChangeCountPer100km', item: '百公里车道偏离次数', details: 0, score: 0, proportionScore: 0 },
  { key: 'nearCollisionCountPer100km', item: '百公里前向碰撞预警次数', details: 0, score: 0, proportionScore: 0 },
  { key: 'rapidAccelDecelCountPer100km', item: '百公里急加减速次数', details: 0, score: 0, proportionScore: 0 },
  { key: 'emergencyBrakeCountPer100km', item: '百公里紧急制动次数', details: 0, score: 0, proportionScore: 0 },
];

const INITIAL_DRIVING_FOCUS: AssessmentDataItem[] = [
  { key: 'longEyeClosureCountPer100km', item: '百公里疲劳驾驶次数', details: 0, score: 0, proportionScore: 0 },
  { key: 'distractionCountPer100km', item: '百公里分心驾驶次数', details: 0, score: 0, proportionScore: 0 },
  { key: 'smokingCountPer100km', item: '百公里抽烟次数', details: 0, score: 0, proportionScore: 0 },
];

/**
 * 安全评估区域属性
 */
interface SafetyAssessmentSectionProps {
  isViewMode: boolean;
  driverId?: number | string;
}

/**
 * 安全评估区域组件
 */
const SafetyAssessmentSection: React.FC<SafetyAssessmentSectionProps> = ({
  isViewMode,
  driverId,
}) => {
  const [loading, setLoading] = useState(false);
  const [latestAssessmentLevel, setLatestAssessmentLevel] = useState('');
  const [individualInfo, setIndividualInfo] = useState<AssessmentDataItem[]>(INITIAL_INDIVIDUAL_INFO);
  const [drivingViolations, setDrivingViolations] = useState<AssessmentDataItem[]>(INITIAL_DRIVING_VIOLATIONS);
  const [accidentHistory, setAccidentHistory] = useState<AssessmentDataItem[]>(INITIAL_ACCIDENT_HISTORY);
  const [drivingBehavior, setDrivingBehavior] = useState<AssessmentDataItem[]>(INITIAL_DRIVING_BEHAVIOR);
  const [drivingFocus, setDrivingFocus] = useState<AssessmentDataItem[]>(INITIAL_DRIVING_FOCUS);
  
  // 防抖定时器
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 提交数据到服务器
  const submitData = useCallback(async () => {
    if (!driverId || isViewMode) {
      return;
    }

    try {
      // 构建提交数据 - 将所有表格数据转换回API格式
      const submitData: any = {
        riskLevel: latestAssessmentLevel === '暂无' ? '' : latestAssessmentLevel,
      };

      // 司机个体信息
      individualInfo.forEach(item => {
        if (item.key === 'age') {
          submitData.age = item.details;
          submitData.driverInfoScore = item.score;
        } else {
          submitData[item.key] = item.details;
        }
      });

      // 驾驶违规情况
      drivingViolations.forEach(item => {
        submitData[item.key] = item.details;
      });

      // 历史事故出险情况
      accidentHistory.forEach(item => {
        if (item.key === 'claimCount') {
          submitData.claimCount = item.details;
          submitData.accidentHistoryScore = item.score;
        } else {
          submitData[item.key] = item.details;
        }
      });

      // 当期驾驶行为情况
      drivingBehavior.forEach(item => {
        if (item.key === 'frequentLaneChangeCountPer100km') {
          submitData.frequentLaneChangeCountPer100km = item.details;
          submitData.drivingHabitScore = item.score;
        } else {
          submitData[item.key] = item.details;
        }
      });

      // 当期驾驶专注性评价
      drivingFocus.forEach(item => {
        if (item.key === 'longEyeClosureCountPer100km') {
          submitData.longEyeClosureCountPer100km = item.details;
          submitData.mentalStateScore = item.score;
        } else {
          submitData[item.key] = item.details;
        }
      });

      // const response = await editDriverSafetyAssessment(submitData);
      
      // if (response.data?.success) {
      //   // console.log('安全评估数据保存成功');
      // } else {
      //   message.error(response.data?.msg || '保存失败');
      // }
    } catch (error) {
      console.error('保存安全评估数据失败:', error);
      message.error('保存失败，请重试');
    }
  }, [driverId, isViewMode, latestAssessmentLevel, individualInfo, drivingViolations, accidentHistory, drivingBehavior, drivingFocus]);

  // 防抖提交
  const debouncedSubmit = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      submitData();
    }, 1000); // 1秒防抖
  }, [submitData]);

  // 数据变化时触发防抖提交
  useEffect(() => {
    if (!isViewMode && driverId) {
      debouncedSubmit();
    }
  }, [individualInfo, drivingViolations, accidentHistory, drivingBehavior, drivingFocus, isViewMode, driverId, debouncedSubmit]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // 加载数据
  const loadData = async () => {
    if (!driverId) {
      console.log('没有司机ID，跳过加载安全评估数据');
      return;
    }
    
    setLoading(true);
    try {
      const response = await getDriverSafetyAssessment(driverId);
      if (response.data?.success && response.data?.data) {
        const data = response.data.data;
        
        // 设置风险等级
        setLatestAssessmentLevel(data.riskLevel || '暂无');
        
        // 司机个体信息
        setIndividualInfo([
          { key: 'age', item: '年龄分布', details: data.age || 0, score: data.driverInfoScore || 0, proportionScore: 0 },
          { key: 'drivingExperience', item: '驾龄分布', details: data.drivingExperience || 0, score: 0, proportionScore: 0 },
          { key: 'safetyAwareness', item: '安全意识', details: data.safetyAwareness || 0, score: 0, proportionScore: 0 },
          { key: 'personalityTrait', item: '性格', details: data.personalityTrait || 0, score: 0, proportionScore: 0 },
          { key: 'communicationSkill', item: '沟通能力', details: data.communicationSkill || 0, score: 0, proportionScore: 0 },
        ]);
        
        // 驾驶违规情况
        setDrivingViolations([
          { key: 'speedingCountPer100km', item: '超速', details: data.speedingCountPer100km || 0, score: 0, proportionScore: 0 },
          { key: 'phoneUseCountPer100km', item: '接打电话', details: data.phoneUseCountPer100km || 0, score: 0, proportionScore: 0 },
        ]);
        
        // 历史事故出险情况
        setAccidentHistory([
          { key: 'claimCount', item: '档案登记的出险次数', details: data.claimCount || 0, score: data.accidentHistoryScore || 0, proportionScore: 0 },
          { key: 'afterServiceScore', item: '售后服务中的分值', details: data.afterServiceScore || 0, score: 0, proportionScore: 0 },
        ]);
        
        // 当期驾驶行为情况
        setDrivingBehavior([
          { key: 'frequentLaneChangeCountPer100km', item: '百公里车道偏离次数', details: data.frequentLaneChangeCountPer100km || 0, score: data.drivingHabitScore || 0, proportionScore: 0 },
          { key: 'nearCollisionCountPer100km', item: '百公里前向碰撞预警次数', details: data.nearCollisionCountPer100km || 0, score: 0, proportionScore: 0 },
          { key: 'rapidAccelDecelCountPer100km', item: '百公里急加减速次数', details: data.rapidAccelDecelCountPer100km || 0, score: 0, proportionScore: 0 },
          { key: 'emergencyBrakeCountPer100km', item: '百公里紧急制动次数', details: data.emergencyBrakeCountPer100km || 0, score: 0, proportionScore: 0 },
        ]);
        
        // 当期驾驶专注性评价
        setDrivingFocus([
          { key: 'longEyeClosureCountPer100km', item: '百公里疲劳驾驶次数', details: data.longEyeClosureCountPer100km || 0, score: data.mentalStateScore || 0, proportionScore: 0 },
          { key: 'distractionCountPer100km', item: '百公里分心驾驶次数', details: data.distractionCountPer100km || 0, score: 0, proportionScore: 0 },
          { key: 'smokingCountPer100km', item: '百公里抽烟次数', details: data.smokingCountPer100km || 0, score: 0, proportionScore: 0 },
        ]);
      } else {
        // 清空数据，保留初始值结构
        setLatestAssessmentLevel('暂无');
        setIndividualInfo(INITIAL_INDIVIDUAL_INFO);
        setDrivingViolations(INITIAL_DRIVING_VIOLATIONS);
        setAccidentHistory(INITIAL_ACCIDENT_HISTORY);
        setDrivingBehavior(INITIAL_DRIVING_BEHAVIOR);
        setDrivingFocus(INITIAL_DRIVING_FOCUS);
      }
    } catch (error) {
      console.error('加载安全评估数据失败:', error);
      setLatestAssessmentLevel('暂无');
      setIndividualInfo(INITIAL_INDIVIDUAL_INFO);
      setDrivingViolations(INITIAL_DRIVING_VIOLATIONS);
      setAccidentHistory(INITIAL_ACCIDENT_HISTORY);
      setDrivingBehavior(INITIAL_DRIVING_BEHAVIOR);
      setDrivingFocus(INITIAL_DRIVING_FOCUS);
    } finally {
      setLoading(false);
    }
  };

  // 组件创建时加载数据
  useEffect(() => {
    loadData();
  }, [driverId]);
  return (
    <>
      <div style={{ marginBottom: 16 }}>
        最新评估等级：{latestAssessmentLevel || '暂无'}
      </div>

      <SafetyAssessmentTable
        isViewMode={isViewMode}
        title="司机个体信息"
        data={individualInfo}
        onChange={setIndividualInfo}
      />

      <SafetyAssessmentTable
        isViewMode={isViewMode}
        title="驾驶违规情况"
        data={drivingViolations}
        onChange={setDrivingViolations}
      />

      <SafetyAssessmentTable
        isViewMode={isViewMode}
        title="历史事故出险情况"
        data={accidentHistory}
        onChange={setAccidentHistory}
      />

      <SafetyAssessmentTable
        isViewMode={isViewMode}
        title="当期驾驶行为情况"
        data={drivingBehavior}
        onChange={setDrivingBehavior}
      />

      <SafetyAssessmentTable
        isViewMode={isViewMode}
        title="当期驾驶专注性评价"
        data={drivingFocus}
        onChange={setDrivingFocus}
      />
    </>
  );
};

export default SafetyAssessmentSection;
